import styled, { keyframes } from "styled-components";

export const INK = "var(--color-text)";
export const TEAL = "var(--color-primary)";
export const TEAL_DARK = "#00878a";
export const MISSING = "#9aa9b7";
export const SNOW = "#f9fafc";
export const HAIRLINE = "#e3e6e8";

/** The value rendered for anything the response did not carry. */
export const MISSING_VALUE = "-";

export const SectionLabel = styled.div<{ $gap?: number }>`
  font-weight: 700;
  font-size: 12px;
  line-height: 1.28;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${INK};
  padding-bottom: ${(props) => props.$gap ?? 8}px;
`;

export const Block = styled.div<{ $snow?: boolean }>`
  border-top: 1px solid ${HAIRLINE};
  background: ${(props) => (props.$snow ? SNOW : "#fff")};
  padding: 10px 14px 12px;
`;

/** Label-over-value leaf, shared by Well Parameters and Spacing Metrics. */
export const StatCell = styled.div`
  min-width: 0;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  line-height: 1.25;
  color: #3c4858;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StatValue = styled.div<{ $missing?: boolean }>`
  font-family: var(--fontMono);
  font-size: 14px;
  color: ${(props) => (props.$missing ? MISSING : INK)};
`;

export const StatGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
  gap: 12px 18px;
`;

/*
 * Table treatment, measured off the real data table rather than approximated:
 *
 * - 14px on the header and the cells — react-data-grid's `--font-size`, which
 *   the grid root sets and every cell inherits.
 * - 700 on the header — rdg's header-row rule.
 * - The header band is the label colour at 12.5% alpha. `DataTable.tsx` builds
 *   it as `column.color + "20"`, and hex `20` is 32/255.
 * - `var(--fontMono)` cells, as `CellWrapper` in `DataTable.tsx`.
 * - `var(--color-text-10)` borders — `DataTable.scss` overrides every
 *   `.rdg-cell` border to `rgba(var(--color-text-rgb), 0.1)`, and the same file
 *   gives header cells a heavier 0.18 separator.
 * - Row hover `hsl(0deg 0% 96%)` — rdg's `--row-hover-background-color`, which
 *   `DataTable.scss` applies on `.rdg-row:hover`. Named here as a literal
 *   because that variable is defined inside the grid and does not resolve
 *   outside it.
 */

export const TableFrame = styled.div`
  border: 1px solid var(--color-text-10);
  border-radius: 2px;
  overflow: hidden;
`;

export const TableHeaderRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.$columns};
  background: rgba(var(--color-primary-rgb), 0.125);
  border-bottom: 1px solid rgba(var(--color-text-rgb), 0.18);
`;

export const TableHeaderCell = styled.span`
  padding: 7px 10px;
  font-size: 14px;
  font-weight: 700;
  color: ${TEAL_DARK};
  border-right: 1px solid rgba(var(--color-text-rgb), 0.18);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
`;

export const TableRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.$columns};
  border-bottom: 1px solid var(--color-text-10);

  &:hover {
    background: hsl(0deg 0% 96%);
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.span<{ $missing?: boolean }>`
  padding: 6px 10px;
  font-family: var(--fontMono);
  font-size: 14px;
  color: ${(props) => (props.$missing ? MISSING : INK)};
  border-right: 1px solid var(--color-text-10);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
`;

export const CellUnit = styled.span`
  color: #3c4858;
  font-size: 12px;
`;

/** A product label column, then five value columns. */
export const PRODUCT_TABLE_COLUMNS = "128px repeat(5, 1fr)";

export const ProductTableSection = styled.div`
  border-top: 1px solid #e3e6e8;
  padding: 10px 14px 14px;
`;

export const ProductCell = styled.span`
  padding: 6px 10px;
  font-size: 14px;
  color: ${INK};
  border-right: 1px solid var(--color-text-10);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const Swatch = styled.span<{ $color: string }>`
  width: 11px;
  height: 11px;
  background: ${(props) => props.$color};
  flex: none;
`;

// ── Empty and loading states ─────────────────────────────────────────────────

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
`;

export const EmptyTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #3c4858;
`;

export const EmptyDetail = styled.div`
  font-size: 12px;
  color: #8395a5;
  text-align: center;
  max-width: 380px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const SkeletonBar = styled.div<{ $width?: string; $height?: number }>`
  width: ${(props) => props.$width ?? "100%"};
  height: ${(props) => props.$height ?? 12}px;
  background: #eef1f6;
  border-radius: 4px;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;
